<template>
    <BannerFooterOptions
        v-if="sandboxMode || canManageMasterTemplates"
        :model-value="optionsVisible"
        :options="footerOptions"
        @update:modelValue="emit('updateOptionsVisible', $event)"
    />

    <MasterTemplateReplaceButton
        ref="replace"
        :master-template-id="masterTemplate._id"
        @updateStarted="emit('updateStarted')"
        @familyUpdated="emit('familyUpdated', $event)"
        @updated="emit('update')"
    />
</template>

<script lang="ts" setup>
import type { MasterTemplate } from "hoxton-types";

import { computed, useTemplateRef } from "vue";
import { useRoute } from "vue-router";
import { Modal } from "view-ui-plus";
import { TemplateType } from "hoxton-shared/enums/masterTemplate";

import BannerFooterOptions from "@/components/Previews/BannerFooter/BannerFooterOptions.vue";
import MasterTemplateReplaceButton from "@/components/Campaign/MasterTemplates/MasterTemplateReplaceButton.vue";

import * as auth from "@/services/Auth";
import { useStore } from "vuex";
import type { RootState } from "@/store/types";

const route = useRoute();
const replace = useTemplateRef("replace");
const store = useStore<RootState>();

const clientId = route.params.clientId as string;
const campaignId = route.params.campaignId as string;

const {
    resizeMode = undefined,
    optionsVisible,
    masterTemplate,
    isMetaPreview
} = defineProps<{
    resizeMode?: boolean;
    masterTemplate: MasterTemplate;
    optionsVisible: boolean;
    isMetaPreview: boolean;
}>();

const emit = defineEmits<{
    (e: "update"): void;
    (e: "familyUpdated", value: string[]): void;
    (e: "updateStarted"): void;
    (e: "updateOptionsVisible", value: boolean): void;
    (e: "toggleResizeMode", value: boolean): void;
    (e: "remove"): void;
    (e: "newSize"): void;
}>();

const canManageMasterTemplates = computed(() =>
    auth.userCan(auth.Actions.CanManageMasterTemplates, { clientId, campaignId })
);

const isAE = computed(() => masterTemplate.adType === TemplateType.AE);
const isHTML5 = computed(() => masterTemplate.adType === TemplateType.HTML5);
const sandboxMode = computed(() => store.state.editor.isSandboxMode);
const isChildResize = computed(() => !!masterTemplate.parentId);

const footerOptions = computed(() => {
    const options = [];

    if (isHTML5.value && resizeMode !== undefined && !isMetaPreview) {
        options.push({
            id: "resize-button",
            label: resizeMode ? "Exit Resize Mode" : "Resize Mode",
            icon: "md-expand",
            onClick: toggleResizeMode
        });
    }

    // Child resize templates: only resize-related actions and delete
    if (isChildResize.value) {
        if (!sandboxMode.value) {
            options.push({
                id: "delete-button",
                label: "Remove from campaign",
                icon: "md-trash",
                onClick: deleteTemplatePrompt
            });
        }

        return options;
    }

    // Master templates
    if (isHTML5.value && !isMetaPreview) {
        options.push({
            id: "new-size-button",
            label: "New size...",
            icon: "ios-flash",
            onClick: () => emit("newSize")
        });
    }

    if (!isAE.value && !sandboxMode.value) {
        options.push({
            id: "replace-button",
            label: "Replace Template",
            icon: "md-cloud-upload",
            onClick: onReplace
        });
    }

    if (!sandboxMode.value) {
        options.push({
            id: "delete-button",
            label: "Remove from campaign",
            icon: "md-trash",
            onClick: deleteTemplatePrompt
        });
    }

    return options;
});

function toggleResizeMode() {
    emit("toggleResizeMode", !resizeMode);
}

function hideFooterOptions() {
    emit("updateOptionsVisible", false);
}

async function onReplace() {
    hideFooterOptions();

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    replace.value.remoteClick();
}

function deleteTemplatePrompt() {
    hideFooterOptions();

    Modal.confirm({
        title: "Are you sure?",
        content:
            "Removing this template from this campaign will delete all ads produced with it. This is permanent and can not be undone.",
        okText: "Remove template",
        onOk: () => emit("remove"),
        cancelText: "Cancel",
        width: 300
    });
}
</script>
